import { convertToHHSS } from '@/services/utils';
export function useTimeEntryDates() {
  /**
   * Normalizes a time value to HH:MM format
   * @param {Date|string} timeValue - The time value to normalize
   * @param {Date} defaultTime - Default time to use if timeValue is invalid
   * @returns {string} Time in HH:MM format
   */
  const normalizeTimeToHHMM = (timeValue, defaultTime = new Date()) => {
    try {
      if (timeValue instanceof Date) {
        return convertToHHSS(timeValue);
      } else if (typeof timeValue === 'string') {
        // If already in HH:MM format, return as is
        if (/^\d{2}:\d{2}/.test(timeValue)) {
          return timeValue;
        }
        // Try to parse as date string
        const date = new Date(timeValue);
        if (!isNaN(date.getTime())) {
          return convertToHHSS(date);
        }
      }
    } catch (error) {
      console.warn('Error normalizing time:', error);
    }
    return convertToHHSS(defaultTime);
  };

  /**
   * Converts HH:MM string to Date object with proper error handling
   * @param {string} timeString - Time in HH:MM format
   * @param {Date} baseDate - Base date to use for the time
   * @returns {Date|null} Date object or null if invalid
   */
  const convertTimeStringToDate = (timeString, baseDate) => {
    if (!timeString || typeof timeString !== 'string') return null;
    try {
      const timeMatch = timeString.match(/^(\d{1,2}):(\d{2})/);
      if (!timeMatch) return null;
      const hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        console.warn('Invalid time values:', {
          hours,
          minutes
        });
        return null;
      }
      const dateTime = new Date(baseDate);
      dateTime.setHours(hours, minutes, 0, 0);
      return dateTime;
    } catch (error) {
      console.error('Error converting time string to date:', error);
      return null;
    }
  };

  /**
   * Creates reasonable start/end times based on hours
   * @param {number} hours - Number of hours
   * @param {Date} date - Base date
   * @param {number} defaultStartHour - Default start hour (default: 9)
   * @returns {Object} Object with startTime and endTime Date objects
   */
  const createStartEndTimes = (hours, date, defaultStartHour = 9) => {
    const startTime = new Date(date);
    startTime.setHours(defaultStartHour, 0, 0, 0);
    const endTime = new Date(startTime.getTime() + hours * 60 * 60 * 1000);
    return {
      startTime,
      endTime
    };
  };

  /**
   * Handles cross-day time entries
   * @param {Date} startTime - Start time
   * @param {Date} endTime - End time
   * @returns {Object} Object with adjusted times and a flag indicating if it crosses days
   */
  const handleCrossDayEntry = (startTime, endTime) => {
    // If end time is before start time, assume it's on the next day
    if (endTime < startTime) {
      const adjustedEndTime = new Date(endTime);
      adjustedEndTime.setDate(adjustedEndTime.getDate() + 1);
      return {
        startTime,
        endTime: adjustedEndTime,
        crossesDay: true
      };
    }
    return {
      startTime,
      endTime,
      crossesDay: false
    };
  };

  /**
   * Validates time entry dates
   * @param {Date} startTime - Start time
   * @param {Date} endTime - End time
   * @returns {Object} Validation result with isValid flag and error message
   */
  const validateTimeEntry = (startTime, endTime) => {
    if (!startTime || !endTime) {
      return {
        isValid: false,
        error: 'Start and end times are required'
      };
    }
    if (!(startTime instanceof Date) || !(endTime instanceof Date)) {
      return {
        isValid: false,
        error: 'Invalid date objects'
      };
    }
    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
      return {
        isValid: false,
        error: 'Invalid date values'
      };
    }

    // Handle cross-day scenario
    let actualEndTime = endTime;
    if (endTime < startTime) {
      actualEndTime = new Date(endTime);
      actualEndTime.setDate(actualEndTime.getDate() + 1);
    }

    // Check for unreasonable durations (more than 24 hours)
    const duration = actualEndTime.getTime() - startTime.getTime();
    const maxDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    if (duration > maxDuration) {
      return {
        isValid: false,
        error: 'Time entry duration cannot exceed 24 hours'
      };
    }
    if (duration < 0) {
      return {
        isValid: false,
        error: 'End time must be after start time'
      };
    }
    return {
      isValid: true
    };
  };
  return {
    normalizeTimeToHHMM,
    convertTimeStringToDate,
    createStartEndTimes,
    handleCrossDayEntry,
    validateTimeEntry
  };
}